import React, { Component, ReactNode } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'redux-scaffolding-ts';
import { Button, Grid, Icon, Message, Table } from 'semantic-ui-react';
import { Query, OrderDefinition } from 'stores/dataStore';
import { nameof } from 'utils/object';
import { TableModel, TableView } from 'widgets/collections/table';
import { ChecklistReportStore, ChecklistReportDto, ChecklistReportEventDto } from 'stores/checklist-reports/checklist-report-store';
import { buildChecklistReportFilter } from './checklist-report-query';

export interface ChecklistReportListProps extends RouteComponentProps, WithTranslation {
  checklistReportStore?: ChecklistReportStore;
  filters?: Record<string, any>;
  refreshTick?: number;
  hideHeaderActions?: boolean;
  mainActionsFilters?: ReactNode;
  onReset?: () => void;
}

interface ChecklistReportListState {
  query: Query;
  grouped: boolean;
}

const DEFAULT_QUERY_PARAMETERS = {
  ShowDeleted: 'false'
};

const PAGE_OPTIONS = [
  { text: '10 / page', value: 10 },
  { text: '20 / page', value: 20 },
  { text: '30 / page', value: 30 },
  { text: '50 / page', value: 50 }
];

const DEFAULT_ORDER_BY: OrderDefinition[] = [
  { direction: 'Descending', field: nameof<ChecklistReportEventDto>('eventId'), useProfile: false }
];

const getChecklistStatuses = (item: ChecklistReportEventDto) =>
  Array.from(new Set((item.checklistItems || []).map(checklistItem => checklistItem.status).filter(Boolean)));

const getChecklistInstructors = (item: ChecklistReportEventDto) =>
  Array.from(new Set((item.checklistItems || []).map(checklistItem => checklistItem.instructor).filter(Boolean)));

const groupChecklistItemsByInstructor = (item: ChecklistReportEventDto) => {
  const groups = new Map<string, ChecklistReportEventDto['checklistItems']>();

  (item.checklistItems || []).forEach(checklistItem => {
    const instructor = checklistItem.instructor || '-';
    const checklistItems = groups.get(instructor) || [];
    checklistItems.push(checklistItem);
    groups.set(instructor, checklistItems);
  });

  return Array.from(groups, ([instructor, checklistItems]) => ({ instructor, checklistItems }));
};

const isGroupedEvent = (item: ChecklistReportDto): item is ChecklistReportEventDto => 'checklistItems' in item;

@connect(['checklistReportStore', ChecklistReportStore])
class ChecklistReportList extends Component<ChecklistReportListProps, ChecklistReportListState> {
  state: ChecklistReportListState = {
    grouped: false,
    query: {
      searchQuery: '',
      orderBy: DEFAULT_ORDER_BY,
      skip: 0,
      take: 10,
      parameters: DEFAULT_QUERY_PARAMETERS
    }
  };

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps: ChecklistReportListProps) {
    if (prevProps.filters !== this.props.filters) {
      this.setState(({ query }) => ({ query: { ...query, skip: 0 } }), this.load);
    } else if (prevProps.refreshTick !== this.props.refreshTick) {
      this.load();
    }
  }

  private load = () => {
    const { filters } = this.props;
    const filter = buildChecklistReportFilter(filters || {});
    const query = { ...this.state.query, filter };
    this.setState({ query }, () =>
      this.props.checklistReportStore.getFromEndpointAsync(
        this.state.query,
        this.state.grouped ? 'get-checklist-reports-grouped' : 'get-checklist-reports'
      )
    );
  };

  private handleToggleView = () => {
    this.setState(state => ({ grouped: !state.grouped, query: { ...state.query, skip: 0 } }), this.load);
  };

  private handleOrderBy = (orderBy: OrderDefinition[]) => {
    this.setState(({ query }) => ({ query: { ...query, orderBy: [...orderBy] } }), this.load);
  };

  private handlePageChange = (skip: number, take: number) => {
    this.setState(({ query }) => ({ query: { ...query, skip, take } }), this.load);
  };

  private handleExport = () => {
    this.props.checklistReportStore.exportToExcel(this.state.query).catch(() => undefined);
  };

  // Parent clearing filters triggers the reload through componentDidUpdate.
  private handleReset = () => {
    const { onReset } = this.props;
    this.setState(({ query }) => ({ query: { ...query, orderBy: DEFAULT_ORDER_BY, skip: 0 } }), onReset || this.load);
  };

  private handleRowDoubleClick = (item: ChecklistReportDto) => {
    this.props.history.push(`/events/${item.eventGuid}`, {
      returnTo: '/checklist-reports',
      initialTab: this.props.t('CheckList')
    });
  };

  render() {
    const {
      t,
      mainActionsFilters,
      checklistReportStore: { state: storeState }
    } = this.props;

    const tableModel = {
      columns: [
        {
          title: t('Event Type'),
          renderer: (data: ChecklistReportDto) => <span style={{ whiteSpace: 'nowrap' }}>{data?.eventType}</span>,
          sortDefinition: { field: 'eventType', useProfile: false }
        },
        {
          title: t('Event ID'),
          renderer: (data: ChecklistReportDto) => <a className="clickable-icon">{data?.eventId}</a>,
          sortDefinition: { field: 'eventId', useProfile: false, active: 'Descending' }
        },
        {
          title: t('Machine Model'),
          renderer: (data: ChecklistReportDto) => data?.machineModel,
          sortDefinition: { field: 'machineModel', useProfile: false }
        },
        {
          title: t('Location'),
          renderer: (data: ChecklistReportDto) => data?.location,
          sortDefinition: { field: 'location', useProfile: false }
        },
        {
          title: t('Instructor'),
          renderer: (data: ChecklistReportDto, expanded: boolean) =>
            isGroupedEvent(data) ? (expanded ? '' : getChecklistInstructors(data).join(', ') || '-') : data.instructor || '-',
          sortDefinition: { field: 'instructor', useProfile: false }
        },
        {
          title: t('Status'),
          renderer: (data: ChecklistReportDto) =>
            isGroupedEvent(data) ? getChecklistStatuses(data).join(', ') || '-' : data.status || '-',
          sortDefinition: { field: 'status', useProfile: false }
        },
        {
          title: t('CheckList Item'),
          renderer: (data: ChecklistReportDto) =>
            isGroupedEvent(data) ? null : (
              <span style={{ color: '#4183c4' }}>
                <Icon name={data.checked ? 'check square outline' : 'square outline'} color={data.checked ? 'green' : 'grey'} />
                {data.checkListItem || '-'}
              </span>
            ),
          sortDefinition: { field: 'checkListItem', useProfile: false }
        },
        {
          title: t('Comment'),
          renderer: (data: ChecklistReportDto) => (isGroupedEvent(data) ? null : data.comment),
          sortDefinition: { field: 'comment', useProfile: false }
        },
        {
          title: t('Completion Rate'),
          renderer: (data: ChecklistReportDto) => (data != null ? `${data.completedChecklistItems}/${data.totalChecklistItems}` : ''),
          sortDefinition: { field: 'completionRate', useProfile: false }
        }
      ],
      data: storeState,
      keySelector: (item: ChecklistReportDto) => (isGroupedEvent(item) ? item.eventGuid : item.id),
      showExpander: (item: ChecklistReportDto) => isGroupedEvent(item) && (item.checklistItems || []).length > 0,
      expandedRenderer: (item: ChecklistReportDto) => {
        if (!isGroupedEvent(item)) return <></>;
        const hasMultipleStatuses = getChecklistStatuses(item).length > 1;
        const instructorGroups = groupChecklistItemsByInstructor(item);
        return (
          <>
            {instructorGroups.map(group =>
              group.checklistItems.map((checklistItem, index) => (
                <Table.Row key={checklistItem.id} className="custom-table-expanded-row">
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell />
                  <Table.Cell>{index === 0 ? group.instructor : ''}</Table.Cell>
                  <Table.Cell>{hasMultipleStatuses ? checklistItem.status || '-' : ''}</Table.Cell>
                  <Table.Cell style={{ color: '#4183c4' }}>
                    <Icon
                      name={checklistItem.checked ? 'check square outline' : 'square outline'}
                      color={checklistItem.checked ? 'green' : 'grey'}
                    />
                    {checklistItem.checkListItem || '-'}
                  </Table.Cell>
                  <Table.Cell>{checklistItem.comment}</Table.Cell>
                  <Table.Cell />
                  <Table.Cell />
                </Table.Row>
              ))
            )}
          </>
        );
      },
      pagination: {
        pageSize: this.state.query.take,
        activePage: Math.floor(this.state.query.skip / this.state.query.take) + 1,
        options: PAGE_OPTIONS
      }
    } as TableModel<ChecklistReportDto>;

    return (
      <div className="checklist-reports__table-wrapper">
        {storeState.result && !storeState.result.isSuccess && (storeState.result.messages || []).length > 0 && (
          <Message
            className="error-message__style"
            icon="exclamation circle"
            error
            header={t('An error ocurred')}
            list={storeState.result.messages.map(o => o.body)}
            onDismiss={this.props.checklistReportStore.clearMessages}
          />
        )}
        <div className="event-wrapper">
          <Grid className="event-types-list-grid">
            <Grid.Row className="event-types-list-items-row request-list__table-view">
              <TableView
                model={tableModel}
                onOrderByChanged={this.handleOrderBy}
                onRefresh={this.load}
                onPageChange={this.handlePageChange}
                onRowDoubleClick={this.handleRowDoubleClick}
                mainActionsFilters={mainActionsFilters}
                canCreateNew={false}
                canEdit={false}
                canDelete={false}
                canExpandRows={true}
                extraMenu={[
                  {
                    className: 'checklist-reports-reset',
                    content: (
                      <Button icon size="mini" data-tooltip={this.props.t('Reset')}>
                        <Icon name="sort" />
                      </Button>
                    ),
                    onClick: this.handleReset
                  },
                  {
                    className: 'checklist-reports-export',
                    content: (
                      <Button
                        icon
                        size="mini"
                        disabled={storeState.isBusy}
                        className="checklist-reports-export-button"
                        data-tooltip={this.props.t('Export')}
                      >
                        <Icon name="download" />
                      </Button>
                    ),
                    onClick: this.handleExport
                  },
                  {
                    className: 'checklist-reports-view-mode',
                    content: (
                      <Button
                        icon
                        size="mini"
                        disabled={storeState.isBusy}
                        data-tooltip={t(this.state.grouped ? 'Show flat rows' : 'Show grouped events')}
                      >
                        <Icon name={this.state.grouped ? 'list' : 'sitemap'} />
                      </Button>
                    ),
                    onClick: this.handleToggleView
                  }
                ]}
              />
            </Grid.Row>
          </Grid>
        </div>
      </div>
    );
  }
}

export default withTranslation()(ChecklistReportList);
